// Read the rendered frame back, on either of the node renderer's backends.
//
// WHY THIS EXISTS AT ALL. Reading a canvas needs `preserveDrawingBuffer`, and
// three's node renderer has no such option — the string appears nowhere in
// `three/webgpu`, on either backend — so `gl.readPixels`, `canvas.toDataURL()`
// and `drawImage(canvas, …)` all return a cleared buffer, or throw on a null
// context because a WebGPU canvas has no WebGL one to ask. The failure is silent
// in the worst way: not an error, a BLACK IMAGE.
//
// So the frame is rendered AGAIN, into a target this module owns, and that is
// read. A render target is not subject to the drawing buffer's lifetime, so
// nothing has to be preserved, and both backends can read one back.
//
// THREE THINGS ARE LOAD-BEARING and each was wrong first:
//
//   - THE TARGET COMES FROM THE RENDERER'S OWN MODULE. `three` and `three/webgpu`
//     are separate instances, so a RenderTarget built from the wrong one is not a
//     valid key in the other's internal WeakMaps. It fails as `TypeError: Invalid
//     value used as weak map key`, thrown from inside three, naming nothing.
//   - THE TWO READBACKS HAVE DIFFERENT SHAPES. `readRenderTargetPixelsAsync`
//     RETURNS the data; passing a buffer makes it a `textureIndex` and you get
//     the same weak-map error from a completely different cause.
//   - THE BACKENDS DISAGREE ABOUT WHERE y=0 IS. WebGPU reads through
//     `copyTextureToBuffer` (top-left origin); the WebGL2 fallback reads a
//     framebuffer with raw `gl.readPixels` (bottom-left). Asking whether the
//     METHOD exists answers the wrong question — both backends have it — so the
//     flip is keyed on the BACKEND, or the fallback silently samples the wrong
//     half. That is the path no developer box takes.
//
// And the target carries the renderer's own `outputColorSpace`: three picks a
// material's output encoding from the CURRENT render target, so a plain one
// returns LINEAR values while the canvas shows sRGB — every comparison against an
// authored colour is then wrong by the transfer curve, with nothing to say so.
// Same trap, and the same fix, as `reflectionBlendTarget.js`.

import * as THREE from 'three'

/** Scratch for the drawing-buffer size — these run per sample. */
const _dims = new THREE.Vector2()

/** Per-renderer target, kept so a repeated read does not reallocate. */
const _targets = new WeakMap()

/** True when reading gives rows top-down (WebGPU) rather than bottom-up (WebGL). */
export function readsTopDown(renderer) {
  return renderer?.backend?.isWebGPUBackend === true
}

/**
 * The renderer's drawing-buffer size, which is NOT the CSS size times a pixel
 * ratio: a renderer may clamp the ratio, and a target even slightly larger than
 * what is drawn leaves an unrendered band that reads as black.
 */
export function bufferSize(renderer) {
  const d = renderer.getDrawingBufferSize(_dims)
  return { w: Math.max(1, Math.round(d.x)), h: Math.max(1, Math.round(d.y)) }
}

async function targetFor(renderer, w, h) {
  const held = _targets.get(renderer)
  if (held && held.width === w && held.height === h) return held
  held?.dispose()
  const { WGPU } = await import('../ogs/render/three/threeSplatDeps.js')
  const target = new WGPU.RenderTarget(w, h, {
    format: THREE.RGBAFormat,
    type: THREE.UnsignedByteType,
    colorSpace: renderer.outputColorSpace,
    depthBuffer: true,
    stencilBuffer: false,
    // The canvas is built without MSAA; a multisampled target would resolve
    // differently from what is on screen.
    samples: 0,
  })
  _targets.set(renderer, target)
  return target
}

/** Render `scene` into the owned target and hand back the raw RGBA rect. */
async function readRect(renderer, scene, camera, x, y, w, h) {
  const size = bufferSize(renderer)
  const target = await targetFor(renderer, size.w, size.h)
  const was = renderer.getRenderTarget()
  renderer.setRenderTarget(target)
  // `render` is async on the node renderer; awaiting a non-promise is harmless.
  await renderer.render(scene, camera)
  renderer.setRenderTarget(was)
  if (typeof renderer.readRenderTargetPixelsAsync === 'function') {
    return { data: await renderer.readRenderTargetPixelsAsync(target, x, y, w, h), size }
  }
  const out = new Uint8Array(w * h * 4)
  renderer.readRenderTargetPixels(target, x, y, w, h, out)
  return { data: out, size }
}

/**
 * One pixel of the rendered frame, as `[r, g, b, a]` 0-255.
 *
 * @param {number} fx fraction of the canvas width
 * @param {number} fy fraction of the canvas height, measured from the TOP — as a
 *   screenshot measures it, because that is what a caller expects of a fraction.
 */
export async function readPixel(renderer, scene, camera, fx, fy) {
  const { w, h } = bufferSize(renderer)
  const topDown = readsTopDown(renderer)
  const x = Math.min(w - 1, Math.max(0, Math.round(w * fx)))
  const y = Math.min(h - 1, Math.max(0, Math.round(h * (topDown ? fy : 1 - fy))))
  const { data } = await readRect(renderer, scene, camera, x, y, 1, 1)
  return [data[0], data[1], data[2], data[3]]
}

/**
 * The whole rendered frame as an `ImageData`, rows top-down, ready for
 * `putImageData` / `drawImage`.
 */
export async function readFrame(renderer, scene, camera) {
  const { w, h } = bufferSize(renderer)
  const { data } = await readRect(renderer, scene, camera, 0, 0, w, h)
  const out = new Uint8ClampedArray(w * h * 4)
  if (readsTopDown(renderer)) {
    out.set(data)
  } else {
    // Bottom-up source: copy row by row into top-down order. ImageData has no
    // notion of origin, so the flip has to happen here or the image is upside down.
    const stride = w * 4
    for (let row = 0; row < h; row++) {
      const src = (h - 1 - row) * stride
      out.set(data.subarray(src, src + stride), row * stride)
    }
  }
  return new ImageData(out, w, h)
}

/** Free the target held for a renderer (its canvas is going away). */
export function disposeReadback(renderer) {
  _targets.get(renderer)?.dispose()
  _targets.delete(renderer)
}
